import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button, RouterButtonLink } from "../../components/Button";
import { Card } from "../../components/Card";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { Container } from "../../components/Container";
import { FormActions, FormActionsStart } from "../../components/FormActions";
import { InfoBox } from "../../components/InfoBox";
import { LoadingContainer } from "../../components/LoadingContainer";
import { PageDetailHeader } from "../../components/PageDetailHeader";
import {
  useCreatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useGetPaymentMethodQuery,
  useUpdatePaymentMethodMutation,
} from "../../query/paymentMethodsQuery";
import { useGetStoreQuery } from "../../query/storesQuery";
import { PaymentMethodType } from "@KassaroAB/shared/types/enums/PaymentMethodType";
import type { PaymentMethodId, StoreId } from "@KassaroAB/shared/types/ids";
import type { RestCreatePaymentMethod } from "@KassaroAB/shared/types/paymentMethods/paymentMethod";
import { PaymentMethodForm } from "./forms/PaymentMethodForm";

const FORM_ID = "payment-method-form";
const NEW_PAYMENT_METHOD_ID = "new";

const getDefaultPaymentMethod = (
  type: PaymentMethodType,
  name: string,
): RestCreatePaymentMethod | null => {
  switch (type) {
    case PaymentMethodType.CASH:
      return { type, name, openCashDrawer: false, useChangeCalculator: false };
    case PaymentMethodType.OTHER:
      return { type, name, openCashDrawer: false, askForAmount: false };
    case PaymentMethodType.CREDIT_NOTE:
    case PaymentMethodType.GIFT_CARD:
      return { type, name, askForAmount: false };
    case PaymentMethodType.SWISH_COMPANY:
    case PaymentMethodType.SWISH_COMMERCE:
      return { type, name, swishNumber: "", messageToCustomer: "" };
    case PaymentMethodType.WESTPAY_CLOUD:
      return {
        type,
        name,
        integrationId: "",
        terminalId: "",
        isUAT: false,
        isLinked: false,
        tipOnScreen: false,
      };
    case PaymentMethodType.MARKETPAY_CLOUD:
      return { type, name, integrationId: "", terminalId: "" };
    case PaymentMethodType.MARKETPAY_LOCAL:
      return { type, name, integrationId: "", ipAddress: "" };
    case PaymentMethodType.NETS_TERMINAL:
      return { type, name, integrationId: "", terminalId: "" };
    case PaymentMethodType.VIVA:
      return { type, name, integrationId: "", isFastRefundEnabled: false };
    case PaymentMethodType.VIVA_CLOUD:
      return { type, name, integrationId: "", terminalId: "" };
    case PaymentMethodType.SOFTPAY:
      return { type, name, integrationId: "" };
    case PaymentMethodType.INVOICE:
      return { type, name, integrationId: "", printSignatureReceipt: false };
    case PaymentMethodType.ORDER:
      return {
        type,
        name,
        integrationId: "",
        printSignatureReceipt: false,
        includeOrderDate: true,
      };
    default:
      return null;
  }
};

export const EditPaymentMethodView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { storeId, paymentMethodId: routePaymentMethodId } = useParams<{
    storeId: StoreId;
    paymentMethodId: PaymentMethodId;
  }>();
  const isCreating = routePaymentMethodId === NEW_PAYMENT_METHOD_ID;
  const paymentMethodId = isCreating ? undefined : routePaymentMethodId;
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [savedTick, setSavedTick] = useState(0);

  const { data: store, isLoading: isStoreLoading } = useGetStoreQuery(storeId);
  const {
    data: paymentMethod,
    isLoading: isPaymentMethodLoading,
    error: loadError,
  } = useGetPaymentMethodQuery(storeId, paymentMethodId);
  const {
    mutate: createPaymentMethod,
    error: createError,
    isPending: isCreatePending,
  } = useCreatePaymentMethodMutation(storeId);
  const {
    mutate: updatePaymentMethod,
    error: updateError,
    isPending: isUpdatePending,
  } = useUpdatePaymentMethodMutation(storeId);
  const {
    mutate: deletePaymentMethod,
    error: deleteError,
    isPending: isDeletePending,
  } = useDeletePaymentMethodMutation(storeId);

  const listPath = `/stores/${storeId}/paymentMethods`;
  const isLoading = isStoreLoading || isPaymentMethodLoading;
  const isSaving = isCreatePending || isUpdatePending;
  const createType = z.enum(PaymentMethodType).safeParse(searchParams.get("type"));
  const newPaymentMethod = createType.success
    ? getDefaultPaymentMethod(
        createType.data,
        t(`paymentMethodType.${createType.data}`, { defaultValue: createType.data }),
      )
    : null;
  const formPaymentMethod = isCreating ? newPaymentMethod : paymentMethod;
  const saveError = createError ?? updateError ?? deleteError;
  const title = isCreating
    ? t("paymentMethodForm.newPaymentMethod")
    : (paymentMethod?.name ?? t("paymentMethodForm.editPaymentMethod"));

  const handleSubmit = (data: RestCreatePaymentMethod) => {
    if (isCreating) {
      createPaymentMethod(data, {
        onSuccess: (created) => {
          setSuccessMessage(t("paymentMethodForm.createdSuccessfully"));
          navigate(`${listPath}/${created.id}`, { replace: true });
        },
      });
      return;
    }
    if (!paymentMethodId) return;
    updatePaymentMethod(
      { paymentMethodId, paymentMethod: data },
      {
        onSuccess: () => {
          setSuccessMessage(t("paymentMethodForm.updatedSuccessfully"));
          setSavedTick((tick) => tick + 1);
        },
        onError: () => setSuccessMessage(null),
      },
    );
  };

  const confirmDelete = () => {
    if (!paymentMethodId) return;
    deletePaymentMethod(paymentMethodId, {
      onSuccess: () => {
        setDeleteOpen(false);
        navigate(listPath);
      },
    });
  };

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  return (
    <Container>
      <PageDetailHeader
        title={title}
        parentLabel={t("paymentMethodsView.title")}
        parentTo={listPath}
        currentLabel={isCreating ? title : (paymentMethod?.name ?? t("common.loading"))}
      />
      {loadError && (
        <InfoBox variant="error">{t("paymentMethodForm.errorLoad")}</InfoBox>
      )}
      {isCreating && !newPaymentMethod && (
        <InfoBox variant="error">{t("paymentMethodForm.invalidType")}</InfoBox>
      )}
      {saveError && <InfoBox variant="error">{saveError.message}</InfoBox>}
      {successMessage && <InfoBox variant="success">{successMessage}</InfoBox>}
      <Card>
        {isLoading ? (
          <LoadingContainer message={t("paymentMethodForm.loading")} />
        ) : formPaymentMethod && store ? (
          <>
            <PaymentMethodForm
              key={paymentMethodId ?? formPaymentMethod.type}
              formId={FORM_ID}
              store={store}
              paymentMethod={formPaymentMethod}
              paymentMethodId={paymentMethodId}
              savedTick={savedTick}
              onSubmit={handleSubmit}
            />
            <FormActions>
              {!isCreating && (
                <FormActionsStart>
                  <Button
                    type="button"
                    $color="danger"
                    onClick={() => setDeleteOpen(true)}
                    disabled={isSaving || isDeletePending}
                  >
                    {t("paymentMethodForm.deletePaymentMethod")}
                  </Button>
                </FormActionsStart>
              )}
              <RouterButtonLink to={listPath} $color="secondary">
                {t("common.cancel")}
              </RouterButtonLink>
              <Button type="submit" form={FORM_ID} disabled={isSaving}>
                {isSaving
                  ? t("common.saving")
                  : isCreating
                    ? t("common.create")
                    : t("common.save")}
              </Button>
            </FormActions>
          </>
        ) : null}
      </Card>
      <ConfirmDialog
        isOpen={deleteOpen}
        title={t("paymentMethodForm.deletePaymentMethod")}
        message={t("paymentMethodForm.deleteConfirmationMessage", {
          name: paymentMethod?.name ?? "",
        })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Container>
  );
};
